import { FacetResult } from 'collaboration-service/dist/dtos/FacetResult';
import { SelectCount, SelectElementContainerWithCount } from 'components/SelectHelpers/SelectHelpers';
import { components, GroupBase, ImgOnChangeValue, ImgSelect, ImgSelectProps, MultiValueProps, OptionProps, SingleValueProps } from 'imaginarity-react-ui';
import { rootElement } from 'index';
import * as _ from "lodash";
import * as React from 'react';
import { useAppTheme } from 'services/useAppTheme';
import { ThemeContext } from 'styled-components';
import { CMSTheme } from 'views/CMS/CMSTheme';
import { createFilledStyles } from 'views/Workflow/State/Operations/CopyToGroupWorkflowTransitionOperation';

export interface FileTypeWithCount {
    value: string;
    label: string;
    count?: number;
}

const Option = (lng?: string) => <IsMulti extends boolean>(p: OptionProps<FileTypeWithCount, IsMulti, GroupBase<FileTypeWithCount>>) => {
    const { data } = p;

    return (
        <components.Option {...p} >
            <SelectElementContainerWithCount>
                <div>{data.label}</div>
                {(data.count ?? 0) > 0 && <SelectCount>{data.count ?? 0}</SelectCount>}
            </SelectElementContainerWithCount>
        </components.Option>
    )
}
const SingleValue = (lng?: string) => <IsMulti extends boolean>(p: SingleValueProps<FileTypeWithCount, IsMulti, GroupBase<FileTypeWithCount>>) => {
    const { data } = p;
    return (
        <components.SingleValue {...p} >
            <SelectElementContainerWithCount>
                <div>{data.label}</div>
                {(data.count ?? 0) > 0 && <SelectCount>{data.count ?? 0}</SelectCount>}
            </SelectElementContainerWithCount>
        </components.SingleValue>
    )
}

const MultiValue = (lng?: string) => <IsMulti extends boolean>(p: MultiValueProps<FileTypeWithCount, IsMulti, GroupBase<FileTypeWithCount>>) => {
    const { data } = p;

    return (
        <components.MultiValue {...p} >
            <SelectElementContainerWithCount>
                <div>{data.label}</div>
                {(data.count ?? 0) > 0 && <SelectCount>({data.count ?? 0})</SelectCount>}
            </SelectElementContainerWithCount>
        </components.MultiValue>
    )
}

export interface CategorySelectionExtendedProps<IsMulti extends boolean> extends Omit<ImgSelectProps<FileTypeWithCount, IsMulti>, "onChange" | "isMulti"> {
    onFileTypeChanged: (value: ImgOnChangeValue<FileTypeWithCount, IsMulti, false>) => void;
    fileTypes: FileTypeWithCount[];
    isMulti: IsMulti;
    counts?: FacetResult[];
    lng?: string;
    menuPortal?: HTMLElement | null;
    specialStyles?: boolean;
}

const FileTypeSelect = <IsMulti extends boolean = true>(p: CategorySelectionExtendedProps<IsMulti>) => {
    const { fileTypes, isMulti, onFileTypeChanged, value, counts, menuPortal, lng, specialStyles, ...rest } = p;
    const [options, setOptions] = React.useState<FileTypeWithCount[]>([]);
    const theme = React.useContext(ThemeContext);
    const cmsTheme = useAppTheme<CMSTheme>("cmsTheme");
    const emptyStyles = React.useMemo(() => createFilledStyles(theme, false, cmsTheme.select.emptyColor, cmsTheme.select.emptyFadings), [theme, cmsTheme.select.emptyColor, cmsTheme.select.emptyFadings]);
    const filledStyles = React.useMemo(() => createFilledStyles(theme, false, cmsTheme.select.filledColor, cmsTheme.select.filledFadings), [theme, cmsTheme.select.filledColor, cmsTheme.select.filledFadings]);

    const so = React.useMemo(() => (fts: FileTypeWithCount[]) => {
        const o = _.map(fts, f => ({ ...f, count: _.find(counts, c => c.data === f.value)?.count ?? 0 }));
        setOptions(_.reverse(_.sortBy(_.reverse(_.sortBy(o, f => f.label)), c => c.count ?? 0)))
    }, [setOptions, counts])
    const val = React.useMemo(() => _.isArray(value) ? _.filter(options, o => _.find(value, v => v?.value === o?.value) !== undefined) : _.find(options, o => o?.value === (value as any)?.value), [value, options]);

    React.useEffect(() => so(fileTypes), [fileTypes, so]);

    if (!options)
        return <div />;
    return (
        <ImgSelect
            onChange={onFileTypeChanged}
            options={options}
            value={val}
            components={{
                Option: Option(lng),
                SingleValue: SingleValue(lng),
                MultiValue: MultiValue(lng),
            }}
            getOptionLabel={o => o.label}
            getOptionValue={o => o.value}
            isMulti={isMulti}
            menuPortalTarget={menuPortal !== undefined ? menuPortal : rootElement}
            closeMenuOnSelect={isMulti ? false : true}
            styles={specialStyles ? ((_.isArray(val) ? val.length > 0 : (val !== null && val !== undefined)) ? filledStyles : emptyStyles) : undefined}
            {...rest}
        />
    );
}
export default FileTypeSelect;